import { useCallback, useEffect, useRef, useState, } from 'react';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import useIsomorphicLayoutEffect from '@polestar/component-warehouse-react/hooks/useIsomorphicLayoutEffect';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { usePortal } from '@polestar/component-warehouse-react/contexts/portal';
import { preventCancelableDefault } from '../helpers/event';
// Check if the browser supports "preventScroll" option on focus
let supportsPreventScroll = false;
try {
    document.createElement('div').focus({
        get preventScroll() {
            supportsPreventScroll = true;
            return true;
        },
    });
}
catch (e) {
    // Ignore
}
const focusableElements = [
    'input:not([disabled]):not([type=hidden])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'a[href]',
    'area[href]',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]',
    '[tabindex]:not([disabled])',
];
const FOCUSABLE_ELEMENT_SELECTOR = focusableElements.join(',');
const TABBABLE_ELEMENT_SELECTOR = focusableElements.join(':not([tabindex="-1"]),') + ':not([tabindex="-1"])';
const TABBABLE_ELEMENT_SELECTOR_WITHOUT_TOOLTIPS = focusableElements.join(':not([tabindex="-1"]),') +
    ':not([tabindex="-1"])' +
    ':not([data-type="tooltip"])';
const isElementVisible = (element) => {
    const { display, visibility } = element.style;
    let isVisible = display !== 'none' && visibility !== 'hidden' && visibility !== 'collapse';
    if (isVisible) {
        const { display: computedDisplay, visibility: computedVisibility } = getComputedStyle(element);
        // Check if element has any actual size on screen
        // this helps when parent has display:none or similar
        const rect = element.getBoundingClientRect();
        isVisible =
            computedDisplay !== 'none' &&
                computedVisibility !== 'hidden' &&
                computedVisibility !== 'collapse' &&
                rect.width > 0 &&
                rect.height > 0;
    }
    return isVisible;
};
/**
 * All items that we can focus on using keyboard Tab
 *
 * @internal
 */
export const getTabbableElements = (container, options = {}) => {
    return [
        ...container === null || container === void 0 ? void 0 : container.querySelectorAll((options === null || options === void 0 ? void 0 : options.skipTooltip) ? TABBABLE_ELEMENT_SELECTOR_WITHOUT_TOOLTIPS : TABBABLE_ELEMENT_SELECTOR),
    ];
};
/**
 * All items that we can focus on using element.focus()
 *
 * @internal
 */
export const getFocusableElements = (container) => {
    return [...container === null || container === void 0 ? void 0 : container.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR)];
};
/**
 * Focus on element, check if it's still on the page
 *
 * @internal
 */
export const focusElement = (element, { preventScroll = false, scrollIntoView = true, delayed = true } = {}) => {
    const handleFocus = () => {
        var _a;
        if (element) {
            if (document.contains(element)) {
                const { focusPreventScroll: preventScrollOption } = element.dataset;
                const shouldPreventScroll = preventScrollOption === 'true' || preventScroll;
                if (shouldPreventScroll) {
                    if (supportsPreventScroll) {
                        element.focus({
                            preventScroll: true,
                        });
                    }
                    else {
                        // current scroll
                        const scrollX = window.scrollX;
                        const scrollY = window.scrollY;
                        element.focus();
                        // Put back
                        window.scrollTo(scrollX, scrollY);
                    }
                }
                else {
                    element.focus();
                    if (scrollIntoView) {
                        (_a = element.scrollIntoView) === null || _a === void 0 ? void 0 : _a.call(element, {
                            block: 'center',
                        });
                    }
                }
            }
        }
    };
    if (delayed) {
        requestAnimationFrame(handleFocus);
    }
    else {
        handleFocus();
    }
};
/**
 * Trap the focus rotation inside the container.
 *
 * ```js
 * import { useFocusTrap } from '@polestar/component-warehouse-react';
 *
 * const MyComponent = forwardRef<HTMLDivElement, MyComponentProps>((props, ref) => {
 *    const internalRef = useRef<HTMLDivElement>();
 *    const [trap, setTrap] = useState(true);
 *
 *    useFocusTrap(internalRef, trap);
 *
 *    return <div>
 *      <div ref={internalRef}>
 *        <button>Tab to focus me</button>
 *        <button>Tab twice to focus me</button>
 *      </div>
 *      <button>You can't tab to me</button>
 *    </div>
 * }
 * ```
 *
 * Note: to ensure page transitions do not interfere ensure that the 2nd parameter (`trap` above) is always defined even if it is not trapping the focus
 *
 * @storybook Hooks/useFocusTrap
 *
 * @param containerRef:Ref<T>
 * @param trap:boolean
 * @returns RefCallback<T>
 */
export const useFocusTrap = (containerRef, trap) => {
    const focusedNode = useRef(null);
    const { container: portalContainer } = usePortal();
    useIsomorphicLayoutEffect(() => {
        const container = containerRef.current;
        if (!trap || !container) {
            return;
        }
        const onKeyDown = (e) => {
            if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) {
                return;
            }
            const focusedElement = document.activeElement;
            if (!focusedElement || !container.contains(focusedElement)) {
                return;
            }
            preventCancelableDefault(e);
            const elements = getTabbableElements(container);
            const index = elements.indexOf(focusedElement);
            const lastPosition = elements.length - 1;
            const direction = e.shiftKey ? -1 : 1;
            let nextIndex = index;
            let looped = 0;
            while (elements.length > 0) {
                nextIndex = nextIndex + direction;
                if (nextIndex < 0) {
                    nextIndex = lastPosition;
                }
                else if (nextIndex > lastPosition) {
                    nextIndex = 0;
                }
                if (isElementVisible(elements[nextIndex])) {
                    focusElement(elements[nextIndex]);
                    break;
                }
                else {
                    if (nextIndex === 0) {
                        looped++;
                    }
                }
                // if none of the things are visible, prevent endless loop
                if (looped > 2) {
                    break;
                }
            }
        };
        const onFocus = (e) => {
            // Check if the item being focused is in the container
            // This can happen when you focus from outside the viewport (url bar for example)
            const isInCurrentScope = container.contains(e.target);
            const isInPortal = portalContainer === null || portalContainer === void 0 ? void 0 : portalContainer.contains(e.target);
            if (isInPortal) {
                // the focus trap is meant to keep focus on elements that are shown
                // but the Portal is used by AttachedPopover and others that are on
                // top and therefore should be ignored, while the AttachedPopover
                // will trigger the focus on the element
                // it should also be ignored because only 1 Portal is always active.
                return;
            }
            if (!isInCurrentScope && container.getAttribute('aria-hidden') !== 'true') {
                focusElement(focusedNode.current);
            }
            else {
                focusedNode.current = e.target;
            }
        };
        document.addEventListener('keydown', onKeyDown, false);
        document.addEventListener('focusin', onFocus, false);
        container.addEventListener('focusin', onFocus, false);
        return () => {
            document.removeEventListener('keydown', onKeyDown, false);
            document.removeEventListener('focusin', onFocus, false);
            container.removeEventListener('focusin', onFocus);
        };
    }, [containerRef, trap]);
};
/**
 * Keep current activeElement in memory, and on unmount, put the focus back on that element
 *
 * @internal
 */
export const useFocusRestore = (restore = true, focusOptions) => {
    useIsomorphicLayoutEffect(() => {
        const nodeToRestore = document.activeElement;
        return () => {
            if (restore) {
                focusElement(nodeToRestore, focusOptions);
            }
        };
    }, [restore]);
};
/**
 * Create events and state to be used in a focus group.
 * For example a list of buttons that have a single tab index, and can be navigated with keyboard
 *
 * @internal
 */
export const useFocusGroup = ({ ref: focusContainer, initialFocusIndex = 0, orientation = Orientations.vertical, onKeyDown, onFocus, allowTab = false, preventFocusLoop = false, }) => {
    const [focusIndex, setFocusIndex] = useState(initialFocusIndex);
    const groupId = useSSRSafeId();
    const focusOptions = useRef(null);
    const setFocusIndexAndFocus = useCallback((newIndex) => {
        focusOptions.current = { focus: true };
        setFocusIndex(newIndex);
    }, []);
    useEffect(() => {
        var _a;
        if (((_a = focusOptions === null || focusOptions === void 0 ? void 0 : focusOptions.current) === null || _a === void 0 ? void 0 : _a.focus) && focusContainer.current) {
            focusOptions.current = null;
            const focusable = getFocusableElements(focusContainer.current).find((node) => node.dataset.group === groupId && node.tabIndex === 0);
            focusElement(focusable, { scrollIntoView: false, delayed: false });
        }
    }, [focusIndex, groupId, focusContainer]);
    const handleNext = useCallback(() => {
        const activeElement = document.activeElement;
        if (!focusContainer.current) {
            return;
        }
        // The list of focusable elements is correct
        const focusable = getFocusableElements(focusContainer.current).filter((node) => node.dataset.group === groupId);
        // Find next element in list of focusable elements, also correct (apart from disabledDate range, but we have another function for that part)
        let nextNode = focusable.find((n) => !!(activeElement.compareDocumentPosition(n) &
            (Node.DOCUMENT_POSITION_FOLLOWING | Node.DOCUMENT_POSITION_CONTAINED_BY)));
        if (!nextNode) {
            if (!preventFocusLoop) {
                nextNode = focusable[0];
            }
        }
        if (nextNode) {
            focusElement(nextNode, { scrollIntoView: false, delayed: false });
        }
        return nextNode;
    }, [preventFocusLoop, focusContainer, groupId]);
    const handlePrevious = useCallback(() => {
        const activeElement = document.activeElement;
        if (!focusContainer.current) {
            return;
        }
        const focusable = getFocusableElements(focusContainer.current)
            .filter((node) => node.dataset.group === groupId)
            .reverse();
        let previousNode = focusable.find((n) => !!(activeElement.compareDocumentPosition(n) &
            (Node.DOCUMENT_POSITION_PRECEDING | Node.DOCUMENT_POSITION_CONTAINED_BY)));
        if (!previousNode) {
            if (!preventFocusLoop) {
                previousNode = focusable[0];
            }
        }
        if (previousNode) {
            focusElement(previousNode, { scrollIntoView: false, delayed: false });
        }
        return previousNode;
    }, [preventFocusLoop, focusContainer, groupId]);
    const isHorizontal = orientation === Orientations.horizontal;
    const isVertical = orientation === Orientations.vertical;
    const isBoth = !isHorizontal && !isVertical;
    const handleKeyDown = useCallback((event) => {
        // Ignore arrow keys when modifier keys are pressed
        const usesModifier = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
        if (((isHorizontal || isBoth) && event.key === 'ArrowRight' && !usesModifier) ||
            ((isVertical || isBoth) && event.key === 'ArrowDown' && !usesModifier) ||
            (allowTab && event.key === 'Tab' && !event.shiftKey)) {
            preventCancelableDefault(event);
            handleNext();
        }
        else if (((isHorizontal || isBoth) && event.key === 'ArrowLeft' && !usesModifier) ||
            ((isVertical || isBoth) && event.key === 'ArrowUp' && !usesModifier) ||
            (allowTab && event.key === 'Tab' && event.shiftKey)) {
            preventCancelableDefault(event);
            handlePrevious();
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    }, [allowTab, handleNext, handlePrevious, isBoth, isHorizontal, isVertical, onKeyDown]);
    const handleFocus = useCallback((e) => {
        const index = parseInt(e.currentTarget.dataset.index);
        setFocusIndex(index);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }, [onFocus]);
    // To get memoization to work, we need to use a ref to the focusable elements
    // return the same object if no changes to the focusProps have been made
    const focusPropsRef = useRef([]);
    const createFocusProps = useCallback((index, options) => {
        const props = {
            onKeyDown: handleKeyDown,
            onFocus: handleFocus,
            'data-index': index,
            tabIndex: !!(options === null || options === void 0 ? void 0 : options.forceTabIndex) || (!(options === null || options === void 0 ? void 0 : options.fakeFocus) && index === focusIndex) ? 0 : -1,
            'data-group': groupId,
        };
        let hasChanges = !focusPropsRef.current[index];
        if (!hasChanges) {
            for (const key in props) {
                // @ts-ignore
                if (props[key] !== focusPropsRef.current[index][key]) {
                    hasChanges = true;
                    break;
                }
            }
        }
        if (hasChanges) {
            focusPropsRef.current[index] = props;
        }
        return focusPropsRef.current[index];
    }, [groupId, focusIndex, handleFocus, handleKeyDown]);
    return {
        handleNext,
        handlePrevious,
        focusIndex,
        setFocusIndex,
        setFocusIndexAndFocus,
        createFocusProps,
    };
};
/**
 * Can be used to focus the first element by tabIndex in the container.
 *
 * ```js
 * import { useFocusInitial } from '@polestar/component-warehouse-react';
 *
 * const MyComponent = () => {
 *    const internalRef = useRef<HTMLDivElement>();
 *    const [isVisible, setVisible] = useState(false);
 *
 *    useFocusInitial(internalRef, isVisible);
 *
 *    return <XXXElement ref={internalRef} />
 * }
 * ```
 *
 * On touch devices the autofocus is not default behaviour (because it will
 * pop up a virtual keyboard) and therefore (if needed) you could use the native
 * `autofocus` attribute like:
 *
 * ```js
 * <ComponentXXX autoFocus/>
 * ```
 *
 * @storybook Hooks/useFocusInitial
 *
 */
export const useFocusInitial = (trapContainer, focus = false, { index = 0, preventScroll = true, scrollIntoView = true, delayed = true, } = {}) => {
    useIsomorphicLayoutEffect(() => {
        if (focus && (trapContainer === null || trapContainer === void 0 ? void 0 : trapContainer.current)) {
            const alreadyFocusedInContainer = trapContainer.current.contains(document.activeElement);
            if (!alreadyFocusedInContainer) {
                const all = getTabbableElements(trapContainer.current, { skipTooltip: true });
                focusElement((all === null || all === void 0 ? void 0 : all[index]) || (all === null || all === void 0 ? void 0 : all[0]), { preventScroll, scrollIntoView, delayed });
            }
        }
    }, [trapContainer, focus]);
};
